import React from "react";

import Button from "@common/components/Button/Button";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import {
  forwardingOptions,
  getIntegrationTypeName,
  IntegrationLayoutEntry,
  IntegrationType,
} from "@common/models/integrations";
import { Trello } from "@common/models/integrations/Trello.model";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { BoardOption, useTrelloIntegrationFormConsumer } from "./consumers/useTrelloIntegrationFormConsumer";

interface TrelloIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Trello;
  selectedAppId: string;
  onFinish: () => void;
}

export const TrelloIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: TrelloIntegrationModalProps) => {
  const {
    disabled,
    loading,
    listOption,
    listOptions,
    boardOption,
    boardOptions,
    forwardingOption,
    handleFormChange,
    handleFormSubmit,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useTrelloIntegrationFormConsumer({ integration, selectedAppId, onFinish });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(disabled, boardOption, listOption, forwardingOption, integrationLayout, integration) &&
      !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.TRELLO}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.TRELLO)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.TRELLO)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="boardOption"
          value={boardOption}
          options={boardOptions}
          placeholder="Please choose"
          label="Board"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
        />
        <SelectDropdown
          name="listOption"
          value={listOption}
          options={listOptions}
          placeholder="Please choose"
          label="List"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || !boardOption || loading}
        />
        {boardOption?.value ? (
          <IntegrationLayoutSettings
            integrationLayout={integrationLayout}
            moveEntryUp={moveIntegrationLayoutEntryUp}
            moveEntryDown={moveIntegrationLayoutEntryDown}
            toggleEntry={toggleIntegrationLayoutEntry}
            metadataKeys={metadataKeys}
            metadataEnabledState={metadataEnabledState}
            toggleMetadataKey={toggleMetadataKey}
            toggleAllMetadataKeys={toggleAllMetadataKeys}
            setMetadataKeysEnabled={setMetadataKeysEnabled}
            resetMetadataKeys={resetMetadataKeys}
          />
        ) : null}
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  boardOption: Option,
  listOption: Option,
  forwardingOption: Option,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Trello,
) => {
  if (disabled) return true;
  if (!boardOption || !forwardingOption) return true;
  if (boardOption.value === "to_trello") return false;

  const parsedBoardOption = JSON.parse(boardOption.value || "{}") as BoardOption;
  if (
    integration &&
    listOption &&
    listOption.value !== "" &&
    (parsedBoardOption.boardId !== integration?.board_id ||
      !!forwardingOption.value !== integration?.automatic_forward ||
      listOption.value !== integration?.list_id)
  ) {
    return false;
  }

  // Check if integration layout has changed
  return !(integration && hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout));
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
